import CustomTooltip from '../../shared/CustomTooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BlockIcon from '@mui/icons-material/Block';
import DoneIcon from '@mui/icons-material/Done';
import {
    Box,
    Grid,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useGetCompanyGuestsInfoQuery,
    useGetIndividualClientQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import {
    CustomChip,
    CustomSmallListChip,
} from '../../../layout/styledcomponents/CustomChip';
import GenericTable, { tableBodyCellsStyles } from '../../shared/GenericTable';
import ProfilePicture from '../Profile/ProfilePicture';

export default function IndividualGuestsComponent() {
    const navigate = useNavigate();

    const [url, setUrl] = useState('');
    const urlRef = window.location.href.split('/')[4];
    const { data: getIndividualGuestCompany, isFetching } =
        useGetIndividualClientQuery(url);

    const { data: guestCompanyInfo } = useGetCompanyGuestsInfoQuery(
        `/guests/${urlRef}`,
    );

    useEffect(() => {
        if (urlRef !== undefined) {
            setUrl(`/guests/${urlRef}/users`);
        }
    }, []);

    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const onClickGoBack = () => {
        navigate(`/guests`);
    };

    const theme = useTheme();

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorFn: (row) =>
                `${row?.firstName ?? ''} ${row?.lastName ?? ''}`.trim(),
            id: 'fullName',
            header: 'User',
            size: 261,
            mantineTableBodyCellProps: {
                style: {
                    ...tableBodyCellsStyles,
                    paddingTop: 0,
                    paddingBottom: 0,
                },
            },
            Cell: ({ row }) => {
                const isMobile = useMediaQuery('(max-width:600px)');
                const data = row.original;

                return (
                    <List>
                        <ListItem sx={{ padding: '0!important' }}>
                            {!isMobile && (
                                <ListItemAvatar>
                                    <ProfilePicture
                                        profileDetails={data}
                                        src={data?.profilePicture?.src}
                                        sx={{
                                            width: '40px',
                                            height: '40px',
                                            fontSize: '14px',
                                        }}
                                    />
                                </ListItemAvatar>
                            )}
                            <ListItemText
                                primary={
                                    <Typography>
                                        {data?.firstName} {data?.lastName} -{' '}
                                        <CustomSmallListChip
                                            size='small'
                                            style={{
                                                backgroundColor:
                                                    data.role?.includes('admin')
                                                        ? theme.palette.info
                                                              .main
                                                        : theme.palette
                                                              .lightGrey.main,
                                                color: data.role?.includes(
                                                    'admin',
                                                )
                                                    ? theme.palette.neutral.main
                                                    : theme.palette.midGrey
                                                          .main,
                                            }}
                                            label={
                                                data.role === 'guest_admin'
                                                    ? 'COMPANY ADMIN'
                                                    : data.role ===
                                                        'guest_department_admin'
                                                      ? 'TEAM ADMIN'
                                                      : data.viewOnly === true
                                                        ? 'USER (VIEW ONLY)'
                                                        : 'USER'
                                            }
                                            variant='filled'
                                        />
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                        }}
                                    >
                                        {data?.email}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                );
            },
        },
        {
            accessorFn: (row) => row?.department?.name ?? '',
            id: 'department.name',
            header: 'Team',
            size: 200,
            enableSorting: false,
        },
        {
            accessorKey: 'lastLoginAt',
            header: 'Last Login',
            size: 180,
            mantineTableBodyCellProps: {
                style: {
                    ...tableBodyCellsStyles,
                    paddingTop: 0,
                    paddingBottom: 0,
                },
            },
            Cell: ({ cell }) => {
                const value = cell.getValue<string>();
                return (
                    <List>
                        <ListItem sx={{ padding: '0!important' }}>
                            <ListItemText
                                primary={
                                    <Typography>
                                        {value
                                            ? moment(value).format('DD/MM/YYYY')
                                            : '-'}
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                        }}
                                    >
                                        {value
                                            ? moment(value).format('h:mm a')
                                            : ''}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                );
            },
        },
        {
            id: 'status',
            accessorFn: (row) => (row.isActive ? 'Enabled' : 'Disabled'),
            header: 'Status',
            size: 130,
            filterVariant: 'select',
            filterFn: (row, _columnId, filterValue) => {
                if (!filterValue) return true;
                return row.getValue<string>('status') === filterValue;
            },
            mantineFilterSelectProps: {
                data: [
                    { label: 'Enabled', value: 'Enabled' },
                    { label: 'Disabled', value: 'Disabled' },
                ],
                placeholder: 'Filter by Status',
                clearable: true,
            },
            Cell: ({ cell }) => {
                const isMobile = useMediaQuery('(max-width:600px)');
                const isIpad = useMediaQuery('(max-width:1024px)');
                const value = cell.getValue<string>() === 'Enabled';

                if (isMobile || isIpad) {
                    return value ? (
                        <CustomTooltip title='Enabled'>
                            <DoneIcon
                                sx={{ color: theme.palette.textAccent.main }}
                            />
                        </CustomTooltip>
                    ) : (
                        <CustomTooltip title='Disabled'>
                            <BlockIcon />
                        </CustomTooltip>
                    );
                }

                return (
                    <CustomChip
                        label={
                            <Typography
                                color={value ? 'green' : 'red'}
                                sx={{
                                    fontSize: '12px',
                                }}
                            >
                                {value ? 'Enabled' : 'Disabled'}
                            </Typography>
                        }
                        color={value ? 'green' : 'red'}
                        variant='outlined'
                    />
                );
            },
        },
    ];

    return (
        <Box sx={{ height: 700, width: '100%' }}>
            <GenericTable
                columns={columns}
                data={getIndividualGuestCompany || []}
                tableName={`${guestCompanyInfo?.name}-Users-List`}
                loading={!getIndividualGuestCompany}
                pagination={{ pageSize: 100, pageIndex: 0 }}
                maxHeight={'600px'}
            />
            <Grid
                sx={{
                    width: '100%',
                    height: isMobile ? '80px' : '10%',
                    display: isMobile || isIpad ? 'none' : 'flex', // Hide on mobile
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    // backgroundColor: 'yellow',
                }}
            >
                <CustomButton
                    onClick={onClickGoBack}
                    color='primary'
                    sx={{
                        marginLeft: '-8px',
                        '&:hover': {
                            cursor: 'pointer',
                            color: theme.palette.textAccent.main,
                            backgroundColor: 'transparent',
                            background: 'none',
                        },
                    }}
                >
                    <ArrowBackIcon />{' '}
                    <Typography sx={{ fontSize: '12px', paddingLeft: '5px' }}>
                        Back to Guest Companies
                    </Typography>
                </CustomButton>
            </Grid>
        </Box>
    );
}
